import { Component, Inject, OnInit, signal } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { NotificationType } from '@app/shared/interfaces';
import { Listing, ListingListedStatusChangeReason, PropertiesService } from '@app/shared/services/properties/properties.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';

interface DialogData {
  status: boolean;
  listing: Listing;
}

interface StatusChangeResponse {
  allowed: boolean;
  reasons: {
    value: string;
    label: string;
    has_conditional_input: boolean;
  }[];
}

@Component({
  standalone: false,
  selector: 'sbnb-dialog-update-listing-listed-status',
  templateUrl: './dialog-update-listing-listed-status.html',
})
export class DialogUpdateListingListedStatusComponent implements OnInit {
  loading = signal<boolean>(false);
  updating = signal<boolean>(false);

  statusChangeResponse$: Observable<StatusChangeResponse>;
  allowed: boolean = false;
  error: { name: string; description: string } | null = null;
  selectedReason: string | null = null;
  conditionalReason: string = '';
  availableReasons: ListingListedStatusChangeReason[] = [];

  constructor(
    public dialogRef: MatDialogRef<DialogUpdateListingListedStatusComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private toast: ToastNotificationsService,
    private propertiesService: PropertiesService
  ) { }

  get selectedReasonData(): ListingListedStatusChangeReason | undefined {
    return this.availableReasons.find(r => r.value === this.selectedReason);
  }

  get canUpdate(): boolean {
    if (this.availableReasons?.length && ! this.selectedReason) {
      return false;
    }

    if (this.selectedReasonData?.has_conditional_input && !this.conditionalReason.trim()) {
      return false;
    }

    return true;
  }

  ngOnInit() {
    this.initiateStatusChange();
  }

  private initiateStatusChange() {
    this.loading.set(true);

    this.statusChangeResponse$ = this.propertiesService.getListingListedStatusChangeDetails(
      this.data.listing.property.id,
      this.data.listing.listing_id,
      this.data.listing.platform_key,
      this.data.status,
    ).pipe(
      map(response => ({
        allowed: response.allowed,
        reasons: response.reasons
      })),
      tap(response => {
        this.allowed = response.allowed;

        if (!response.allowed) {
          this.error = {
            name: 'Status Change Not Allowed',
            description: 'The listing status cannot be changed at this time.'
          };

          return;
        }

        this.availableReasons = response.reasons;
      }),
      catchError(error => {
        this.error = {
          name: 'Error',
          description: error.error?.message || 'Failed to check if status can be changed.'
        };
        return of({ allowed: false, reasons: [] });
      }),
      finalize(() => this.loading.set(false))
    );
  }

  selectReason(value: string, hasConditionalInput: boolean) {
    this.selectedReason = value;

    if (!hasConditionalInput) {
      this.conditionalReason = '';
    }
  }

  onConfirm() {
    if (!this.canUpdate) {
      this.toast.open(
        'Please fill all the required fields',
        'OK',
        NotificationType.Error
      );

      return;
    }

    this.updating.set(true);

    this.propertiesService.updateListingListedStatus(
      this.data.listing.property.id,
      this.data.listing.listing_id,
      this.data.listing.platform_key,
      this.data.status,
      { value: this.selectedReason, extra: this.conditionalReason }
    ).pipe(
      tap(() => {
        this.toast.open(
          `Successfully ${this.data.status ? 'listed' : 'unlisted'} your listing`,
          'OK',
          NotificationType.Success
        );
        this.dialogRef.close(true);
      }),
      catchError((error) => {
        this.error = {
          name: error.error?.message || `Failed to ${this.data.status ? 'list' : 'unlist'} your listing`,
          description: (error.error?.errors || []).concat(error.error?.warnings || []).join('. ') || '',
        };

        return of(null);
      }),
      finalize(() => this.updating.set(false))
    ).subscribe();
  }

  onCancel() {
    this.dialogRef.close(false);
  }
}
